import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { cx } from '../cx';
import { Container } from './Layout';
import { Logo, type LogoGround } from './Logo';
import { SOCIAL_ICON_PATHS } from './socialIcons';

export type NavItem = {
  label: string;
  href: string;
  /** Marca a pagina atual (aria-current). */
  current?: boolean;
};

/** Um grupo do cabecalho («Agenda ▾»): abre uma lista de ligacoes. */
export type NavGroup = {
  label: string;
  items: NavItem[];
  /** A pagina aberta pertence a este grupo. */
  current?: boolean;
};

export type NavEntry = NavItem | NavGroup;

const isGroup = (entry: NavEntry): entry is NavGroup => 'items' in entry;

/** A partir de que largura a navegacao sai do «Menu» para o cabecalho. */
export type NavBreakpoint = 'md' | 'lg' | 'xl';

// Classes escritas por extenso para o Tailwind as encontrar.
const SHOW_FROM: Record<NavBreakpoint, string> = { md: 'md:flex', lg: 'lg:flex', xl: 'xl:flex' };
const HIDE_FROM: Record<NavBreakpoint, string> = {
  md: 'md:hidden',
  lg: 'lg:hidden',
  xl: 'xl:hidden',
};

export type SiteHeaderProps = {
  /** Para onde leva o logotipo. */
  homeHref?: string;
  /** Nome do modulo junto ao logotipo («GESTÃO»). Ignorado quando ha `modules`. */
  product?: string;
  /**
   * Seletor de modulos junto ao logotipo, como um breadcrumb de topo
   * («SITE · ARENA · STREAMERS»); ver `moduleNav`. O atual leva `current`.
   */
  modules?: NavItem[];
  /** Ligacoes e grupos (ver `siteNav`). Os grupos abrem com clique ou teclado. */
  nav?: NavEntry[];
  /**
   * A partir de que largura a navegacao aparece no cabecalho; abaixo fica no
   * «Menu». Por omissao `xl` com seletor de modulos, `md` sem ele.
   */
  navFrom?: NavBreakpoint;
  /** Botoes a direita (entrar, carrinho, conta). */
  actions?: ReactNode;
  /**
   * Botoes que, no telemovel, vao para dentro do «Menu» (como «Entrar», quando
   * ao lado do logotipo so cabe «Criar conta»). Acima do `sm` nao aparecem.
   */
  menuActions?: ReactNode;
  ground?: LogoGround;
  className?: string;
};

/**
 * Cabecalho comum aos sites, fixo no topo da janela. Os grupos abrem uma
 * lista por baixo; em ecras pequenos a navegacao recolhe para um botao «Menu»
 * com os mesmos grupos em acordeao.
 */
export function SiteHeader({
  homeHref = '/',
  product,
  modules = [],
  nav = [],
  navFrom,
  actions,
  menuActions,
  ground = 'dark',
  className,
}: SiteHeaderProps) {
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const scrolled = useScrolled();
  const menuId = useId();
  const navRef = useRef<HTMLElement>(null);
  const currentModule = modules.find((m) => m.current);
  const otherModules = modules.filter((m) => !m.current);
  const hasMenu = nav.length > 0 || otherModules.length > 0 || Boolean(menuActions);
  // Com o seletor ao lado do logotipo sobra menos espaco: a navegacao do
  // modulo recolhe para o «Menu» ate ao ecra largo.
  const wide: NavBreakpoint = navFrom ?? (modules.length > 0 ? 'xl' : 'md');

  const close = () => {
    setOpen(false);
    setOpenGroup(null);
  };

  useEffect(() => {
    if (!open && openGroup === null) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setOpenGroup(null);
      }
    };
    // Um clique fora fecha o grupo aberto no cabecalho.
    const onPointer = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) setOpenGroup(null);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open, openGroup]);

  const linkClass = (stacked: boolean) =>
    cx(
      'whitespace-nowrap font-sub text-label font-bold uppercase text-gu-ink-muted transition-colors hover:text-gu-ink',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gu-focus',
      'aria-[current=page]:text-gu-ink aria-[current=page]:underline aria-[current=page]:decoration-gu-accent aria-[current=page]:decoration-2 aria-[current=page]:underline-offset-8',
      stacked && 'block py-3',
    );

  const link = (item: NavItem, stacked: boolean, extra?: string) => (
    <a
      key={item.href}
      href={item.href}
      aria-current={item.current ? 'page' : undefined}
      className={cx(linkClass(stacked), extra)}
      onClick={close}
    >
      {item.label}
    </a>
  );

  return (
    // Fixo no topo; depois de descer, no computador fica um pouco mais baixo e
    // ganha sombra. No telemovel a altura nao muda.
    <header
      data-scrolled={scrolled ? '' : undefined}
      className={cx(
        'sticky top-0 z-40 border-b border-gu-line bg-gu-ground/95 backdrop-blur transition-shadow',
        scrolled && 'shadow-lg shadow-black/40',
        className,
      )}
    >
      <Container
        className={cx(
          'flex min-h-[var(--gu-header-h)] items-center justify-between gap-6 xl:gap-4 2xl:gap-6 transition-[min-height] duration-200',
          scrolled && 'md:min-h-[var(--gu-header-h-scrolled)]',
        )}
      >
        <a
          href={homeHref}
          className="flex items-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gu-focus"
        >
          <Logo
            ground={ground}
            height={20}
            className={cx(
              'h-[var(--gu-logo-h)] w-auto transition-[height] duration-200',
              scrolled && 'md:h-[var(--gu-logo-h-scrolled)]',
            )}
          />
          {product && modules.length === 0 ? (
            <span className="hidden border-l border-gu-line pl-3 font-sub text-label font-bold uppercase text-gu-ink-muted sm:inline">
              {product}
            </span>
          ) : null}
        </a>

        {modules.length > 0 ? (
          <nav aria-label="Módulos Grow uP" className="-ml-3 mr-auto border-l border-gu-line pl-3">
            {/* Em ecras pequenos so o modulo atual; os outros vao para o Menu. */}
            {currentModule ? (
              <span className="font-sub text-label font-bold uppercase text-gu-ink md:hidden">
                {currentModule.label}
              </span>
            ) : null}
            <ul className="hidden items-center gap-1 md:flex">
              {modules.map((item, i) => (
                <li key={item.href} className="flex items-center gap-1">
                  {i > 0 ? (
                    <span aria-hidden="true" className="text-gu-ink-muted">
                      ·
                    </span>
                  ) : null}
                  <a
                    href={item.href}
                    aria-current={item.current ? 'page' : undefined}
                    className={moduleLinkClass}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}

        {nav.length > 0 ? (
          <nav
            ref={navRef}
            aria-label="Principal"
            className={cx('hidden items-center gap-4 xl:gap-3.5 2xl:gap-6', SHOW_FROM[wide])}
          >
            {nav.map((entry) =>
              isGroup(entry) ? (
                <HeaderGroup
                  key={entry.label}
                  group={entry}
                  open={openGroup === entry.label}
                  onToggle={(value) => {
                    setOpenGroup(value ? entry.label : null);
                  }}
                  renderLink={(item) => link(item, false, 'block px-4 py-2')}
                />
              ) : (
                link(entry, false)
              ),
            )}
          </nav>
        ) : null}

        <div className="flex items-center gap-2">
          {actions}
          {hasMenu ? (
            <button
              type="button"
              aria-expanded={open}
              aria-controls={menuId}
              onClick={() => {
                setOpen((value) => !value);
              }}
              className={cx(
                'min-h-11 px-3 font-sub text-label font-bold uppercase text-gu-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gu-focus',
                HIDE_FROM[wide],
              )}
            >
              {open ? 'Fechar' : 'Menu'}
            </button>
          ) : null}
        </div>
      </Container>

      {hasMenu ? (
        <nav
          id={menuId}
          aria-label="Principal"
          hidden={!open}
          // Com o telemovel deitado o menu pode nao caber: desliza por dentro.
          className={cx(
            'max-h-[calc(100dvh-var(--gu-header-h))] overflow-y-auto overscroll-contain border-t border-gu-line',
            HIDE_FROM[wide],
          )}
        >
          <Container>
            {otherModules.length > 0 ? (
              // Os outros modulos so faltam abaixo do md; acima ja estao no seletor.
              <div className="border-b border-gu-line py-3 md:hidden">
                <ul className="-mx-2 flex flex-wrap gap-x-4 gap-y-1">
                  {otherModules.map((item) => (
                    <li key={item.href}>
                      <a href={item.href} className={moduleLinkClass} onClick={close}>
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {nav.map((entry) =>
              isGroup(entry) ? (
                <MenuGroup
                  key={entry.label}
                  group={entry}
                  renderLink={(item) => link(item, true, 'pl-4')}
                />
              ) : (
                link(entry, true)
              ),
            )}
            {menuActions ? (
              // Um clique fecha o menu, como nos links acima.
              <div
                className="flex flex-wrap gap-2 border-t border-gu-line py-3 sm:hidden"
                onClick={close}
              >
                {menuActions}
              </div>
            ) : null}
          </Container>
        </nav>
      ) : null}
    </header>
  );
}

const groupButtonClass = cx(
  'flex items-center gap-1 whitespace-nowrap font-sub text-label font-bold uppercase text-gu-ink-muted transition-colors hover:text-gu-ink',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gu-focus',
  'aria-expanded:text-gu-ink data-[current]:text-gu-ink data-[current]:underline data-[current]:decoration-gu-accent data-[current]:decoration-2 data-[current]:underline-offset-8',
);

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 10 6"
      className={cx('h-1.5 w-2.5 text-gu-accent transition-transform', open && 'rotate-180')}
    >
      <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

/**
 * Um grupo no cabecalho: o botao abre a lista por baixo (clique, Enter ou seta
 * para baixo); as setas andam pela lista, Esc fecha e volta ao botao.
 */
function HeaderGroup({
  group,
  open,
  onToggle,
  renderLink,
}: {
  group: NavGroup;
  open: boolean;
  onToggle: (open: boolean) => void;
  renderLink: (item: NavItem) => ReactNode;
}) {
  const listId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const focusItem = (index: number) => {
    const links = listRef.current?.querySelectorAll('a');
    if (!links || links.length === 0) return;
    links[(index + links.length) % links.length]?.focus();
  };

  const onButtonKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      onToggle(true);
      requestAnimationFrame(() => {
        focusItem(0);
      });
    }
  };

  const onListKey = (event: KeyboardEvent<HTMLUListElement>) => {
    const links = [...(listRef.current?.querySelectorAll('a') ?? [])];
    const at = links.indexOf(document.activeElement as HTMLAnchorElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusItem(at + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusItem(at - 1);
    } else if (event.key === 'Escape') {
      event.stopPropagation();
      onToggle(false);
      buttonRef.current?.focus();
    }
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        data-current={group.current ? '' : undefined}
        className={groupButtonClass}
        onClick={() => {
          onToggle(!open);
        }}
        onKeyDown={onButtonKey}
      >
        {group.label}
        <Chevron open={open} />
      </button>
      <ul
        id={listId}
        ref={listRef}
        hidden={!open}
        onKeyDown={onListKey}
        className="absolute left-0 top-full z-50 mt-3 min-w-[14rem] border border-gu-line border-t-2 border-t-gu-accent bg-gu-ground py-2 shadow-lg shadow-black/40"
      >
        {group.items.map((item) => (
          <li key={item.href}>{renderLink(item)}</li>
        ))}
      </ul>
    </div>
  );
}

/** Um grupo dentro do «Menu»: acordeao, aberto a partida se tem a pagina atual. */
function MenuGroup({
  group,
  renderLink,
}: {
  group: NavGroup;
  renderLink: (item: NavItem) => ReactNode;
}) {
  const [open, setOpen] = useState(Boolean(group.current));
  const listId = useId();
  return (
    <div className="border-b border-gu-line last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        data-current={group.current ? '' : undefined}
        className={cx(groupButtonClass, 'w-full justify-between py-3')}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        {group.label}
        <Chevron open={open} />
      </button>
      <ul id={listId} hidden={!open} className="pb-2">
        {group.items.map((item) => (
          <li key={item.href}>{renderLink(item)}</li>
        ))}
      </ul>
    </div>
  );
}

/** Verdadeiro depois de a pagina descer um pouco. */
function useScrolled(threshold = 8) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const update = () => {
      setScrolled(window.scrollY > threshold);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => {
      window.removeEventListener('scroll', update);
    };
  }, [threshold]);
  return scrolled;
}

const moduleLinkClass = cx(
  'px-2 py-1 font-sub text-label font-bold uppercase text-gu-ink-muted transition-colors hover:text-gu-ink',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gu-focus',
  'aria-[current=page]:text-gu-ink aria-[current=page]:underline aria-[current=page]:decoration-gu-accent aria-[current=page]:decoration-2 aria-[current=page]:underline-offset-8',
);

type FooterLink = { label: string; href: string };

export type SiteFooterProps = {
  /** Grupos de links, cada um com um titulo curto (o mapa do site). */
  columns?: { title: string; links: FooterLink[] }[];
  /** Redes sociais: simbolos numa linha por baixo do logotipo. */
  social?: FooterLink[];
  /** Ligacoes da linha final (privacidade, termos...). */
  legalLinks?: FooterLink[];
  /** Linha final (direitos, NIF). */
  legal?: ReactNode;
  ground?: LogoGround;
  className?: string;
};

const footerLinkClass =
  'text-caption text-gu-ink-muted transition-colors hover:text-gu-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gu-focus';

/** Rodape comum: abre com o filete vermelho a toda a largura, como as paginas do manual. */
export function SiteFooter({
  columns = [],
  social = [],
  legalLinks = [],
  legal,
  ground = 'dark',
  className,
}: SiteFooterProps) {
  return (
    <footer className={cx('border-t-rule border-gu-accent bg-gu-ground', className)}>
      <Container className="flex flex-col gap-10 py-12">
        <div className="flex flex-col gap-10 xl:flex-row xl:justify-between">
          <div className="flex max-w-xs flex-col gap-4">
            <Logo variant="icon" ground={ground} height={56} alt="" />
            <p className="text-caption text-gu-ink-muted">
              Grow uP Esports. Desde 2002 a fazer crescer quem joga.
            </p>
            {social.length > 0 ? (
              <ul aria-label="Redes sociais" className="-ml-2 flex flex-wrap gap-1">
                {social.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      title={link.label}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-10 w-10 items-center justify-center text-gu-ink-muted transition-colors hover:text-gu-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gu-focus"
                    >
                      <SocialIcon label={link.label} />
                      <span className="sr-only">{link.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          {columns.length > 0 ? (
            <nav
              aria-label="Mapa do site"
              className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-5 xl:gap-12"
            >
              {columns.map((column) => (
                <div key={column.title} className="flex flex-col gap-3">
                  <p className="font-sub text-label font-bold uppercase text-gu-ink">
                    {column.title}
                  </p>
                  <ul className="flex flex-col gap-2">
                    {column.links.map((link) => (
                      <li key={link.href}>
                        <a href={link.href} className={footerLinkClass}>
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          ) : null}
        </div>
        {legal || legalLinks.length > 0 ? (
          <div className="flex flex-col gap-3 border-t border-gu-line pt-6 text-caption text-gu-ink-muted sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            {legalLinks.length > 0 ? (
              <ul className="flex flex-wrap gap-x-6 gap-y-2">
                {legalLinks.map((link) => (
                  <li key={link.href}>
                    <a href={link.href} className={footerLinkClass}>
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}
            {legal ? <div>{legal}</div> : null}
          </div>
        ) : null}
      </Container>
    </footer>
  );
}

/** O simbolo de uma rede social; sem simbolo conhecido, a inicial. */
function SocialIcon({ label }: { label: string }) {
  const path = SOCIAL_ICON_PATHS[label];
  if (!path) {
    return (
      <span aria-hidden="true" className="font-sub text-label font-bold">
        {label.slice(0, 1)}
      </span>
    );
  }
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
      <path d={path} />
    </svg>
  );
}
